import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useIdioma } from '../i18n/idioma';
import toast from 'react-hot-toast';
import { supabase } from '../services/supabase';
import { sendChatMessage, ChatMessage, ChatAttachment } from '../services/aiChat';
import { buildHistory } from './aiChatHistory';
import { sendVoiceMessage as sendVoiceMessageAPI, VoiceMode, playAudioBase64 } from '../services/voiceAI';
import { streamingSpeak as ttsSpeak } from '../services/tts/router';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { useNavigate } from 'react-router-dom';

// ── Types ──────────────────────────────────────────────

export interface AIConversation {
  id: string;
  workspace_id: string;
  user_id: string;
  user_name: string | null;
  title: string;
  is_private: boolean;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

export type ConversationFilter = 'mine' | 'team';

export interface PendingChatRequest {
  prompt?: string;
  autoSend?: boolean;
  newConversation?: boolean;
}

export interface OpenContextualChatOptions {
  prompt?: string;
  contextLabel?: string;
  contextualFocus?: string;
  autoSend?: boolean;
  newConversation?: boolean;
}

interface AIChatContextType {
  conversations: AIConversation[];
  conversationsLoading: boolean;
  filter: ConversationFilter;
  setFilter: (f: ConversationFilter) => void;

  activeConversation: AIConversation | null;
  messages: ChatMessage[];
  isLoading: boolean;

  createConversation: () => void;
  loadConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  sendMessage: (text: string, crmContext: any, attachments?: ChatAttachment[]) => Promise<void>;
  togglePrivacy: () => Promise<void>;

  openFullView: () => void;
  openContextualChat: (opts?: OpenContextualChatOptions) => void;
  openBubble: () => void;

  contextualLabel: string | null;
  contextualFocus: string | null;
  pendingChatRequest: PendingChatRequest | null;
  consumePendingChatRequest: () => PendingChatRequest | null;

  isBubbleOpen: boolean;
  setIsBubbleOpen: (open: boolean) => void;

  // Voice AI
  voiceMode: VoiceMode;
  setVoiceMode: (mode: VoiceMode) => void;
  isPlayingAudio: boolean;
  /** One-shot voice message: STT → LLM → TTS, played in-place. Used by the inline mic. */
  sendVoiceMessage: (audioBlob: Blob, crmContext: any) => Promise<void>;
  /**
   * Conversational voice loop turn. Identical pipeline to sendVoiceMessage but
   * does NOT play the audio — returns it for the caller (VoiceConversationModal)
   * to play at its own cadence so the orb can drive playback state.
   */
  submitVoiceTurn: (
    audioBlob: Blob,
    crmContext: any,
    signal?: AbortSignal
  ) => Promise<{ audioBase64?: string | null; transcript: string; reply: string; error?: string }>;

  /**
   * Streaming variant of submitVoiceTurn: routes through voice-ai-stream
   * (SSE) so Gemini's reply tokens flow back as they're generated, and
   * the TTS pipeline fires per-sentence instead of waiting for the full
   * reply. Resolves when the LAST sentence finishes playing. Errors and
   * abort propagate via the signal.
   *
   * Callbacks fire as data arrives:
   *   - onTranscript: once after STT, with the user's transcribed text
   *   - onFirstAudio: when the first PCM sample actually plays
   *   - onReplyText: incremental, with the accumulated reply so far
   */
  submitVoiceTurnStreaming: (
    audioBlob: Blob,
    crmContext: any,
    callbacks?: {
      signal?: AbortSignal;
      onTranscript?: (transcript: string) => void;
      onFirstAudio?: (latencyMs: number) => void;
      onReplyText?: (accumulatedReply: string) => void;
    }
  ) => Promise<{ transcript: string; reply: string; error?: string }>;

  // Full-screen "always listening" voice modal
  isVoiceConversationOpen: boolean;
  openVoiceConversation: () => void;
  closeVoiceConversation: () => void;
}

const AIChatContext = createContext<AIChatContextType | undefined>(undefined);

export const useAIChat = () => {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChat must be used within AIChatProvider');
  return ctx;
};

// ── Provider ───────────────────────────────────────────

export const AIChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useIdioma();
  const { currentUser, isAuthorized } = useAuth();
  const { activeWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<AIConversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [filter, setFilter] = useState<ConversationFilter>('mine');

  const [activeConversation, setActiveConversation] = useState<AIConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isBubbleOpen, setIsBubbleOpenRaw] = useState(false);
  const [contextualLabel, setContextualLabel] = useState<string | null>(null);
  const [contextualFocus, setContextualFocus] = useState<string | null>(null);
  const [pendingChatRequest, setPendingChatRequest] = useState<PendingChatRequest | null>(null);

  const setIsBubbleOpen = useCallback((open: boolean) => {
    setIsBubbleOpenRaw(open);
    if (!open) {
      setContextualLabel(null);
      setContextualFocus(null);
    }
  }, []);

  // Voice AI state
  const [voiceMode, setVoiceModeState] = useState<VoiceMode>(() => {
    try { return (localStorage.getItem('raza-voice-mode') as VoiceMode) || 'full'; }
    catch { return 'full'; }
  });
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isVoiceConversationOpen, setIsVoiceConversationOpen] = useState(false);

  const openVoiceConversation = useCallback(() => setIsVoiceConversationOpen(true), []);
  const closeVoiceConversation = useCallback(() => setIsVoiceConversationOpen(false), []);

  const setVoiceMode = useCallback((mode: VoiceMode) => {
    setVoiceModeState(mode);
    try { localStorage.setItem('raza-voice-mode', mode); } catch { /* ignore */ }
  }, []);

  // Debounce save ref
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeConvRef = useRef<AIConversation | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);

  // Keep refs in sync
  useEffect(() => { activeConvRef.current = activeConversation; }, [activeConversation]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // ── Fetch conversation list ──

  const fetchConversations = useCallback(async () => {
    if (!currentUser || !isAuthorized || activeWorkspace.id === 'default') return;
    setConversationsLoading(true);

    try {
      let query = supabase
        .from('ai_conversations')
        .select('id, workspace_id, title, user_id, user_name, is_private, created_at, updated_at')
        .order('updated_at', { ascending: false })
        .limit(50);

      if (filter === 'mine') {
        query = query.eq('user_id', currentUser.uid);
      } else {
        // Team: shared conversations in the same workspace
        query = query
          .eq('is_private', false)
          .eq('workspace_id', activeWorkspace.id);
      }

      const { data, error } = await query;
      if (error) {
        console.error('[AIChatContext] fetch error:', error);
      } else {
        // Merge with empty messages (we don't load messages in list view)
        setConversations((data || []).map(d => ({ ...d, messages: [] })));
      }
    } finally {
      setConversationsLoading(false);
    }
  }, [currentUser, isAuthorized, filter, activeWorkspace.id]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // ── Save to Supabase (debounced) ──

  const saveToSupabase = useCallback(async (conv: AIConversation, msgs: ChatMessage[]) => {
    if (!conv.id) return;

    const serializedMessages = msgs.map(m => ({
      id: m.id,
      role: m.role,
      text: m.text,
      timestamp: m.timestamp instanceof Date ? m.timestamp.toISOString() : m.timestamp,
      ...(m.attachments && m.attachments.length ? { attachments: m.attachments } : {}),
    }));

    const { error } = await supabase
      .from('ai_conversations')
      .update({
        messages: serializedMessages,
        title: conv.title,
        is_private: conv.is_private,
      })
      .eq('id', conv.id);

    if (error) console.error('[AIChatContext] save error:', error);
  }, []);

  /**
   * Persist the active conversation 1s after the last change.
   *
   * `expectedConvId` binds the write to the conversation the caller was in
   * when it scheduled the save. Without it this resolved BOTH its target and
   * its payload from refs at FIRE time, one second later — so if the user
   * switched conversations while a reply was in flight, the timer read the new
   * conversation as its target and the old conversation's messages as its
   * payload, and `saveToSupabase` does a destructive full replace of the
   * `messages` column. Conversation A's transcript was written over
   * conversation B's row, permanently, with no error and no toast.
   *
   * (RLS keeps this inside one user's own data — UPDATE requires
   * `auth.uid() = user_id` — so it could never corrupt a teammate's row. It
   * could and did destroy your own.)
   */
  const debouncedSave = useCallback((expectedConvId?: string) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const conv = activeConvRef.current;
      const msgs = messagesRef.current;
      if (!conv || msgs.length === 0) return;
      if (expectedConvId && conv.id !== expectedConvId) {
        console.info('[AIChatContext] skipping save: active conversation changed since it was scheduled');
        return;
      }
      saveToSupabase(conv, msgs);
    }, 1000);
  }, [saveToSupabase]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const conv = activeConvRef.current;
      const msgs = messagesRef.current;
      if (conv && msgs.length > 0) {
        saveToSupabase(conv, msgs);
      }
    };
  }, [saveToSupabase]);

  // ── Create conversation ──

  const createConversation = useCallback(() => {
    // Flush current if exists
    if (activeConvRef.current && messagesRef.current.length > 0) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveToSupabase(activeConvRef.current, messagesRef.current);
    }
    setActiveConversation(null);
    setMessages([]);
  }, [saveToSupabase]);

  // ── Load conversation ──

  const loadConversation = useCallback(async (id: string) => {
    // Flush current first
    if (activeConvRef.current && messagesRef.current.length > 0) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      await saveToSupabase(activeConvRef.current, messagesRef.current);
    }

    const { data, error } = await supabase
      .from('ai_conversations')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      console.error('[AIChatContext] load error:', error);
      return;
    }

    const parsedMessages: ChatMessage[] = (data.messages || []).map((m: any) => ({
      ...m,
      timestamp: new Date(m.timestamp),
    }));

    setActiveConversation(data as AIConversation);
    setMessages(parsedMessages);
  }, [saveToSupabase]);

  // ── Delete conversation ──

  const deleteConversation = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('ai_conversations')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('[AIChatContext] delete error:', error);
      return;
    }

    setConversations(prev => prev.filter(c => c.id !== id));

    // If we deleted the active conversation, clear it
    if (activeConvRef.current?.id === id) {
      setActiveConversation(null);
      setMessages([]);
    }
  }, []);

  // ── Send message ──

  const sendMessageFn = useCallback(async (text: string, crmContext: any, attachments?: ChatAttachment[]) => {
    const hasAttachments = !!attachments && attachments.length > 0;
    if ((!text.trim() && !hasAttachments) || isLoading) return;
    setIsLoading(true);

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text,
      timestamp: new Date(),
      ...(hasAttachments ? { attachments } : {}),
    };

    let currentConv = activeConvRef.current;

    // Auto-create conversation if none exists
    if (!currentConv) {
      const title = text.length > 50 ? text.slice(0, 50) + '...' : text;
      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          workspace_id: activeWorkspace.id,
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || 'User',
          title,
          is_private: filter === 'mine',
          messages: [],
        })
        .select()
        .single();

      if (error || !data) {
        console.error('[AIChatContext] create error:', error);
        // Unlike the debounced save (which self-heals on the next write), a
        // failed create silently swallows the user's message — tell them.
        toast.error(t('chat.errores.iniciar_conversacion'));
        setIsLoading(false);
        return;
      }

      currentConv = data as AIConversation;
      setActiveConversation(currentConv);
      // Add to conversations list
      setConversations(prev => [{ ...currentConv!, messages: [] }, ...prev]);
    }

    // The conversation this turn belongs to. Everything below is applied only
    // if it is STILL the active conversation when the reply lands — the user
    // is free to click another conversation while the dots are bouncing.
    const turnConvId = currentConv.id;

    // Append user message
    const updatedMessages = [...messagesRef.current, userMsg];
    setMessages(updatedMessages);

    // Build history for multi-turn. Excludes the message being sent, which
    // travels separately as `message` — including it in both made the model
    // see the user's question twice, as two consecutive user turns.
    const history = buildHistory(messagesRef.current);

    // Call AI
    const { reply, error: aiError } = await sendChatMessage(text, history, crmContext, attachments);

    // Reply landed for a conversation the user has since navigated away from.
    // Dropping it is correct: rendering it would show conversation A's answer
    // under conversation B's title, and persisting it would overwrite B.
    if (activeConvRef.current?.id !== turnConvId) {
      console.info('[AIChatContext] discarding reply: user switched conversations mid-turn');
      setIsLoading(false);
      return;
    }

    const aiMsg: ChatMessage = {
      id: `ai-${Date.now()}`,
      role: 'assistant',
      text: aiError ? `⚠️ ${aiError}` : reply,
      timestamp: new Date(),
      ...(aiError ? { isError: true } : {}),
    };

    // Functional update, never an absolute snapshot: `updatedMessages` was
    // captured before the await, so writing it back would silently drop
    // anything appended during the round trip (a concurrent voice turn, for
    // instance) and resurrect messages the user had already navigated past.
    setMessages(prev => [...prev, aiMsg]);
    setIsLoading(false);

    // Debounced save, bound to the conversation this turn started in.
    debouncedSave(turnConvId);
  }, [isLoading, activeWorkspace.id, currentUser?.displayName, currentUser?.uid, filter, debouncedSave, t]);

  // ── Send voice message ──

  const sendVoiceMessageFn = useCallback(async (audioBlob: Blob, crmContext: any) => {
    if (isLoading) return;
    setIsLoading(true);

    const currentHistory = buildHistory(messagesRef.current);

    const result = await sendVoiceMessageAPI(audioBlob, currentHistory, crmContext, voiceMode, 'nova');

    if (result.error) {
      setIsLoading(false);
      // Show error as AI message
      const errorMsg: ChatMessage = {
        id: `ai-err-${Date.now()}`,
        role: 'assistant',
        text: `⚠️ ${result.error === 'sin-voz' ? t('chat.voz.sinVoz') : result.error}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    // Insert transcribed user message
    const userMsg: ChatMessage = {
      id: `user-voice-${Date.now()}`,
      role: 'user',
      text: `🎙️ ${result.transcript}`,
      timestamp: new Date(),
    };

    let currentConv = activeConvRef.current;

    // Auto-create conversation if none exists
    if (!currentConv) {
      const title = result.transcript.length > 50 ? result.transcript.slice(0, 50) + '...' : result.transcript;
      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          workspace_id: activeWorkspace.id,
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || 'User',
          title: `🎙️ ${title}`,
          is_private: filter === 'mine',
          messages: [],
        })
        .select()
        .single();

      if (error || !data) {
        console.error('[AIChatContext] voice create error:', error);
        setIsLoading(false);
        return;
      }

      currentConv = data as AIConversation;
      setActiveConversation(currentConv);
      setConversations(prev => [{ ...currentConv!, messages: [] }, ...prev]);
    }

    // Insert AI reply message
    const aiMsg: ChatMessage = {
      id: `ai-voice-${Date.now()}`,
      role: 'assistant',
      text: result.reply,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg, aiMsg]);
    setIsLoading(false);
    debouncedSave(currentConv.id);

    // Generate the spoken reply locally via the in-browser TTS (no server cost)
    // and play it as it streams. Skipped in dictation mode (text-only response).
    if (voiceMode === 'full' && result.reply) {
      setIsPlayingAudio(true);
      try {
        await ttsSpeak(result.reply, {
          onFirstAudio: (latencyMs) => {
            console.debug(`[AIChatContext] first audio in ${latencyMs.toFixed(0)}ms`);
          },
          onPlayingComplete: () => setIsPlayingAudio(false),
        });
      } catch (err) {
        console.error('[AIChatContext] TTS streaming error:', err);
        setIsPlayingAudio(false);
      }
    }
  }, [isLoading, voiceMode, activeWorkspace.id, currentUser?.displayName, currentUser?.uid, filter, debouncedSave]);

  // ── Conversational loop turn (used by VoiceConversationModal) ──
  // Same STT→LLM→TTS as sendVoiceMessage but skips inline playback so the
  // VAD-driven modal can play audio + manage the listening/speaking states.
  const submitVoiceTurn = useCallback(async (
    audioBlob: Blob,
    crmContext: any,
    signal?: AbortSignal
  ): Promise<{ audioBase64?: string | null; transcript: string; reply: string; error?: string }> => {
    const currentHistory = buildHistory(messagesRef.current);

    const result = await sendVoiceMessageAPI(audioBlob, currentHistory, crmContext, voiceMode, 'nova');
    if (signal?.aborted) return { transcript: '', reply: '', error: 'aborted' };
    if (result.error) return { transcript: '', reply: '', error: result.error };

    const userMsg: ChatMessage = {
      id: `user-voice-${Date.now()}`,
      role: 'user',
      text: `🎙️ ${result.transcript}`,
      timestamp: new Date(),
    };
    const aiMsg: ChatMessage = {
      id: `ai-voice-${Date.now()}`,
      role: 'assistant',
      text: result.reply,
      timestamp: new Date(),
    };

    let currentConv = activeConvRef.current;
    if (!currentConv) {
      const title = result.transcript.length > 50 ? result.transcript.slice(0, 50) + '...' : result.transcript || 'Voice conversation';
      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          workspace_id: activeWorkspace.id,
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || 'User',
          title: `🎙️ ${title}`,
          is_private: filter === 'mine',
          messages: [],
        })
        .select()
        .single();

      if (error || !data) {
        console.error('[AIChatContext] voice-loop create error:', error);
        return { transcript: result.transcript, reply: result.reply, audioBase64: result.audioBase64, error: t('chat.errores.crear_conversacion_voz') };
      }
      currentConv = data as AIConversation;
      setActiveConversation(currentConv);
      setConversations(prev => [{ ...currentConv!, messages: [] }, ...prev]);
    }

    setMessages(prev => [...prev, userMsg, aiMsg]);
    debouncedSave(currentConv.id);

    // Audio is now generated and played by the caller (the voice modal calls
    // speakStreaming directly using the same abort signal). We just return
    // text. audioBase64 stays null for backward-compat with any caller still
    // checking the field.
    return {
      transcript: result.transcript,
      reply: result.reply,
      audioBase64: null,
    };
  }, [voiceMode, activeWorkspace.id, currentUser?.displayName, currentUser?.uid, filter, debouncedSave]);

  // ── Streaming voice turn (Phase 3) ──
  // Routes through voice-ai-stream (SSE). The user message + a placeholder AI
  // message are inserted into the UI immediately when the transcript arrives,
  // and the AI message text updates progressively as Gemini streams chunks.
  // After Gemini is done, ONE TTS request synthesizes the full reply (smooth
  // playback; per-sentence dispatch caused gaps because server RTF > 1).
  const submitVoiceTurnStreaming = useCallback(async (
    audioBlob: Blob,
    crmContext: any,
    callbacks: {
      signal?: AbortSignal;
      onTranscript?: (transcript: string) => void;
      onFirstAudio?: (latencyMs: number) => void;
      onReplyText?: (accumulatedReply: string) => void;
    } = {}
  ): Promise<{ transcript: string; reply: string; error?: string }> => {
    const currentHistory = buildHistory(messagesRef.current);

    const { playStreamingVoiceReply } = await import('../services/tts/sentenceStreamPlayer');

    const audioBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1] ?? '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(audioBlob);
    });

    // Stable ids assigned up front so we can patch the AI message in-place
    // as text streams in without juggling React keys.
    const turnTs = Date.now();
    const userMsgId = `user-voice-${turnTs}`;
    const aiMsgId = `ai-voice-${turnTs + 1}`;
    let messagesInserted = false;
    let transcript = '';
    let reply = '';

    // Ensure a conversation exists. We do this as soon as the transcript
    // event arrives so the UI navigates to it before the audio starts.
    const ensureConversationCreated = async (firstTranscript: string): Promise<boolean> => {
      if (activeConvRef.current) return true;
      const title = firstTranscript.length > 50
        ? firstTranscript.slice(0, 50) + '...'
        : firstTranscript || 'Voice conversation';
      const { data, error } = await supabase
        .from('ai_conversations')
        .insert({
          workspace_id: activeWorkspace.id,
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || 'User',
          title: `🎙️ ${title}`,
          is_private: filter === 'mine',
          messages: [],
        })
        .select()
        .single();
      if (error || !data) {
        console.error('[AIChatContext] streaming voice-loop create error:', error);
        return false;
      }
      const newConv = data as AIConversation;
      setActiveConversation(newConv);
      setConversations(prev => [{ ...newConv, messages: [] }, ...prev]);
      return true;
    };

    try {
      const result = await playStreamingVoiceReply(audioBase64, currentHistory, crmContext, {
        signal: callbacks.signal,
        onTranscript: async (t) => {
          transcript = t;
          callbacks.onTranscript?.(t);
          // Open the conversation + drop both messages into the UI right
          // away. AI message starts empty and gets filled by onReplyChunk.
          const created = await ensureConversationCreated(t);
          if (!created) return;
          if (!messagesInserted) {
            messagesInserted = true;
            const userMsg: ChatMessage = {
              id: userMsgId,
              role: 'user',
              text: `🎙️ ${t}`,
              timestamp: new Date(),
            };
            const aiMsg: ChatMessage = {
              id: aiMsgId,
              role: 'assistant',
              text: '',
              timestamp: new Date(),
            };
            setMessages(prev => [...prev, userMsg, aiMsg]);
          }
        },
        onReplyChunk: (full) => {
          reply = full;
          callbacks.onReplyText?.(full);
          // Patch the AI message in place — same id, growing text.
          setMessages(prev => prev.map(m =>
            m.id === aiMsgId ? { ...m, text: full } : m
          ));
        },
        onFirstAudio: callbacks.onFirstAudio,
      });
      transcript = result.transcript || transcript;
      reply = result.reply || reply;
    } catch (err) {
      if (callbacks.signal?.aborted) return { transcript, reply, error: 'aborted' };
      const msg = err instanceof Error ? err.message : t('chat.errores.voz_stream');
      // User-side audio quality (background noise / silence) — expected, not a
      // bug. Same regex useVoiceConversation filters on; console.info keeps it
      // out of the admin error logger. Real stream failures stay at error.
      if (/could not understand the audio|no speech detected/i.test(msg)) {
        console.info('[AIChatContext] streaming voice turn skipped (audio quality):', msg);
      } else {
        console.error('[AIChatContext] streaming voice turn failed:', err);
      }
      return { transcript, reply, error: msg };
    }

    if (callbacks.signal?.aborted) return { transcript, reply, error: 'aborted' };
    // Un CÓDIGO, no un mensaje: esto sale de una función suelta y el texto
    // se resuelve donde se pinta, que es donde sí hay idioma.
    if (!transcript) return { transcript: '', reply: '', error: 'sin-voz' };

    // Final write-through to ensure the AI message has the canonical full
    // text (in case the last reply-chunk and done event differ slightly).
    setMessages(prev => prev.map(m =>
      m.id === aiMsgId ? { ...m, text: reply } : m
    ));
    debouncedSave(activeConvRef.current?.id);

    return { transcript, reply };
  }, [activeWorkspace.id, currentUser?.displayName, currentUser?.uid, filter, debouncedSave]);

  // ── Toggle privacy ──

  const togglePrivacy = useCallback(async () => {
    if (!activeConvRef.current) return;
    const conv = activeConvRef.current;
    const newPrivacy = !conv.is_private;

    const { error } = await supabase
      .from('ai_conversations')
      .update({ is_private: newPrivacy })
      .eq('id', conv.id);

    if (error) {
      console.error('[AIChatContext] privacy toggle error:', error);
      return;
    }

    const updated = { ...conv, is_private: newPrivacy };
    setActiveConversation(updated);
    setConversations(prev =>
      prev.map(c => (c.id === conv.id ? { ...c, is_private: newPrivacy } : c))
    );
  }, []);

  // ── Navigate to full view ──

  const openFullView = useCallback(() => {
    navigate('/ai-assistant');
  }, [navigate]);

  const openBubble = useCallback(() => {
    setIsBubbleOpenRaw(true);
  }, []);

  const openContextualChat = useCallback((opts?: OpenContextualChatOptions) => {
    if (opts?.contextLabel) setContextualLabel(opts.contextLabel);
    else setContextualLabel(null);

    if (opts?.contextualFocus) setContextualFocus(opts.contextualFocus);
    else setContextualFocus(null);

    if (opts?.prompt || opts?.autoSend) {
      setPendingChatRequest({
        prompt: opts.prompt,
        autoSend: opts.autoSend ?? false,
        newConversation: opts.newConversation ?? true,
      });
    } else {
      setPendingChatRequest(null);
    }

    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      navigate('/ai-assistant');
    } else {
      setIsBubbleOpenRaw(true);
    }
  }, [navigate]);

  const consumePendingChatRequest = useCallback(() => {
    const req = pendingChatRequest;
    setPendingChatRequest(null);
    return req;
  }, [pendingChatRequest]);

  return (
    <AIChatContext.Provider
      value={{
        conversations,
        conversationsLoading,
        filter,
        setFilter,
        activeConversation,
        messages,
        isLoading,
        createConversation,
        loadConversation,
        deleteConversation,
        sendMessage: sendMessageFn,
        togglePrivacy,
        openFullView,
        openContextualChat,
        openBubble,
        contextualLabel,
        contextualFocus,
        pendingChatRequest,
        consumePendingChatRequest,
        isBubbleOpen,
        setIsBubbleOpen,
        voiceMode,
        setVoiceMode,
        isPlayingAudio,
        sendVoiceMessage: sendVoiceMessageFn,
        submitVoiceTurn,
        submitVoiceTurnStreaming,
        isVoiceConversationOpen,
        openVoiceConversation,
        closeVoiceConversation,
      }}
    >
      {children}
    </AIChatContext.Provider>
  );
};
